/**
 * Auth Context
 *
 * Manages authentication state and organization context.
 * Integrates Clerk auth with Relay API.
 */

import {
  useAuth as useClerkAuth,
  useUser,
  useOrganization,
} from '@clerk/clerk-react';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { setAuthToken, clearAuthToken } from '@/lib/api/client';

interface AuthContextValue {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth is still loading */
  isLoading: boolean;
  /** The API token (if authenticated) */
  token: string | null;
  /** Current organization ID (null = personal workspace) */
  organizationId: string | null;
  /** Current organization name */
  organizationName: string | null;
  /** Whether user is in personal workspace (no org selected) */
  isPersonalWorkspace: boolean;
  /** Sign out */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { isLoaded, isSignedIn, getToken, signOut: clerkSignOut } = useClerkAuth();
  const { user } = useUser();
  const { organization } = useOrganization();

  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get and set auth token
  const setupToken = useCallback(async () => {
    if (!isSignedIn) {
      setToken(null);
      clearAuthToken();
      setIsLoading(false);
      return;
    }

    try {
      // Get Clerk JWT (includes org claims if org is selected)
      const clerkToken = await getToken();
      if (!clerkToken) {
        throw new Error('Failed to get auth token');
      }

      setToken(clerkToken);
      setAuthToken(clerkToken);
    } catch (error) {
      console.error('Auth token error:', error);
      setToken(null);
      clearAuthToken();
    } finally {
      setIsLoading(false);
    }
  }, [isSignedIn, getToken]);

  // Re-fetch token when auth state or organization changes
  useEffect(() => {
    if (isLoaded) {
      setupToken();
    }
  }, [isLoaded, isSignedIn, user?.id, organization?.id, setupToken]);

  // Sign out handler
  const handleSignOut = async () => {
    setToken(null);
    clearAuthToken();
    await clerkSignOut();
  };

  const value: AuthContextValue = {
    isAuthenticated: Boolean(token),
    isLoading: !isLoaded || isLoading,
    token,
    organizationId: organization?.id ?? null,
    organizationName: organization?.name ?? null,
    isPersonalWorkspace: Boolean(isSignedIn && !organization),
    signOut: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
